// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The signature view. Two measures Safe Work Australia publishes separately and
// never crosses: how OFTEN an industry hurts people, against how MUCH each
// injury costs. The interesting industries are the ones off the diagonal.

import { linear, log, ticks } from './layout';
import { svgClose, svgOpen } from './charts';
import { card, chartBox, legend, note, rowActivationAttrs, segmented, viewHead } from './ui';
import { gloss } from '../glossary';
import { esc, money, num, period, rate, tipAttr, weeks } from '../format';
import { DIVISION_COLORS, divisionColor } from '../colors';
import { withDivision } from '../data';
import { median } from '../analysis';
import { attachSvgZoom } from '../utils/svgZoom';
import { openIndustryDrawer } from './industryDrawer';
import type { Bundle, ClaimRow } from '../types';

export const title = 'Cost of harm';

type Row = ClaimRow & { division: string; divisionName: string };

/** State lives in the module so the segmented control survives a re-render. */
let yMeasure: 'comp' | 'lost' = 'comp';

function pickIndex(rows: Row[], key: 'comp' | 'lost', periods: string[]): number {
  for (let i = periods.length - 1; i >= 0; i--) {
    if (rows.some((r) => r[key]?.[i] !== null && r[key]?.[i] !== undefined)) return i;
  }
  return periods.length - 1;
}

function eligible(b: Bundle): { rows: Row[]; idx: number } {
  const rows = withDivision(b.claims.industry);
  const idx = pickIndex(rows, yMeasure, b.claims.periods);
  const usable = rows.filter(
    (r) =>
      r.freq?.[idx] !== null && r.freq?.[idx] !== undefined &&
      r[yMeasure]?.[idx] !== null && r[yMeasure]?.[idx] !== undefined &&
      r.count[idx] !== null && (r.count[idx] as number) >= 200,
  );
  return { rows: usable, idx };
}

function scatter(b: Bundle): string {
  const { rows, idx } = eligible(b);
  const P = b.claims.periods;
  if (rows.length < 4) return `<p class="empty">Not enough industries have both measures published for ${period(P[idx])}.</p>`;

  const W = 880;
  const H = 520;
  const box = { x: 66, y: 18, w: W - 96, h: H - 66 };

  const xs = rows.map((r) => r.freq![idx] as number);
  const ys = rows.map((r) => r[yMeasure]![idx] as number);
  const counts = rows.map((r) => r.count[idx] as number);

  const x = linear([0, Math.max(...xs) * 1.08], [box.x, box.x + box.w]);
  // Claim cost spans an order of magnitude across industries, so a linear axis
  // would pile two thirds of them into the bottom third of the chart.
  const y = log([Math.min(...ys) * 0.85, Math.max(...ys) * 1.15], [box.y + box.h, box.y]);
  const maxCount = Math.max(...counts);
  const r = (c: number) => 4 + Math.sqrt(c / maxCount) * 22;

  const medX = median(xs);
  const medY = median(ys);

  const fmtY = (v: number) => (yMeasure === 'comp' ? `$${(v / 1000).toFixed(0)}k` : `${v.toFixed(0)}w`);
  const fmtYFull = (v: number | null) => (yMeasure === 'comp' ? money(v) : weeks(v));
  const yLabel = yMeasure === 'comp' ? 'Median compensation paid' : 'Median working weeks lost';

  let out = svgOpen(
    W, H, 'chart-svg scatter-svg',
    `Injury frequency against ${yMeasure === 'comp' ? 'median compensation' : 'median weeks lost'} for ${rows.length} industries, ${period(P[idx])}`,
    true,
  );

  // Quadrant shading + the two median reference lines. The whole point of the
  // view is which side of these an industry sits on.
  const px = x(medX);
  const py = y(medY);
  out += `<rect class="quad quad-bad" x="${px.toFixed(1)}" y="${box.y}" width="${(box.x + box.w - px).toFixed(1)}" height="${(py - box.y).toFixed(1)}"/>`;
  out += `<rect class="quad quad-ok" x="${box.x}" y="${py.toFixed(1)}" width="${(px - box.x).toFixed(1)}" height="${(box.y + box.h - py).toFixed(1)}"/>`;

  for (const t of ticks(0, x.domain[1], 6)) {
    const tx = x(t);
    out += `<line class="ax-grid" x1="${tx.toFixed(1)}" y1="${box.y}" x2="${tx.toFixed(1)}" y2="${(box.y + box.h).toFixed(1)}"/>`;
    out += `<text class="ax-txt" x="${tx.toFixed(1)}" y="${(box.y + box.h + 16).toFixed(1)}" text-anchor="middle">${t.toFixed(0)}</text>`;
  }
  const yTickVals = logTicks(y.domain[0], y.domain[1]);
  for (const t of yTickVals) {
    const ty = y(t);
    if (ty < box.y - 1 || ty > box.y + box.h + 1) continue;
    out += `<line class="ax-grid" x1="${box.x}" y1="${ty.toFixed(1)}" x2="${(box.x + box.w).toFixed(1)}" y2="${ty.toFixed(1)}"/>`;
    out += `<text class="ax-txt" x="${(box.x - 7).toFixed(1)}" y="${(ty + 3).toFixed(1)}" text-anchor="end">${fmtY(t)}</text>`;
  }

  out += `<line class="med-line" x1="${px.toFixed(1)}" y1="${box.y}" x2="${px.toFixed(1)}" y2="${(box.y + box.h).toFixed(1)}"/>`;
  out += `<line class="med-line" x1="${box.x}" y1="${py.toFixed(1)}" x2="${(box.x + box.w).toFixed(1)}" y2="${py.toFixed(1)}"/>`;
  out += `<text class="quad-lab" x="${(box.x + box.w - 8).toFixed(1)}" y="${(box.y + 14).toFixed(1)}" text-anchor="end">Often, and expensive</text>`;
  out += `<text class="quad-lab" x="${(box.x + 8).toFixed(1)}" y="${(box.y + 14).toFixed(1)}">Rarely, but expensive</text>`;
  out += `<text class="quad-lab" x="${(box.x + box.w - 8).toFixed(1)}" y="${(box.y + box.h - 8).toFixed(1)}" text-anchor="end">Often, but cheaper</text>`;
  out += `<text class="quad-lab" x="${(box.x + 8).toFixed(1)}" y="${(box.y + box.h - 8).toFixed(1)}">Rarely, and cheaper</text>`;

  out += `<text class="ax-title" x="${(box.x + box.w / 2).toFixed(1)}" y="${(H - 8).toFixed(1)}" text-anchor="middle">Serious claims per million hours worked →</text>`;
  out += `<text class="ax-title" transform="translate(14 ${(box.y + box.h / 2).toFixed(1)}) rotate(-90)" text-anchor="middle">${esc(yLabel)} →</text>`;

  // Largest bubbles first so small ones stay clickable on top of them.
  const order = rows.map((_, i) => i).sort((a, b) => counts[b] - counts[a]);
  for (const i of order) {
    const row = rows[i];
    const cx = x(xs[i]);
    const cy = y(ys[i]);
    const tip = `${row.name}\n${row.divisionName}\n\n${rate(row.freq![idx], 2)} serious claims per million hours worked\n${fmtYFull(row[yMeasure]![idx])} on the median claim\n${num(row.count[idx])} claims in ${period(P[idx])}\n\nClick for the full industry record`;
    out += `<circle class="dot mark-click" cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r(counts[i]).toFixed(1)}" fill="${divisionColor(row.division)}" data-tip="${tipAttr(tip)}" data-code="${esc(row.code)}" tabindex="0" role="button" aria-label="${esc(`${row.name}: ${rate(row.freq![idx], 2)} claims per million hours, ${fmtYFull(row[yMeasure]![idx])} median payment`)}"/>`;
  }

  return out + svgClose;
}

function logTicks(lo: number, hi: number): number[] {
  const out: number[] = [];
  const start = Math.floor(Math.log10(lo));
  const end = Math.ceil(Math.log10(hi));
  for (let e = start; e <= end; e++) {
    for (const m of [1, 2, 5]) {
      const v = m * 10 ** e;
      if (v >= lo && v <= hi) out.push(v);
    }
  }
  return out.length >= 3 ? out : [lo, (lo + hi) / 2, hi];
}

export function render(b: Bundle): string {
  const { rows, idx } = eligible(b);
  const P = b.claims.periods;
  const xs = rows.map((r) => r.freq![idx] as number);
  const ys = rows.map((r) => r[yMeasure]![idx] as number);
  const medX = median(xs);
  const medY = median(ys);
  const rareRuinous = rows.filter((r) => (r.freq![idx] as number) < medX && (r[yMeasure]![idx] as number) > medY);
  const oftenCheap = rows.filter((r) => (r.freq![idx] as number) > medX && (r[yMeasure]![idx] as number) < medY);

  const divisionsPresent = [...new Set(rows.map((r) => r.division))].sort();

  return `
    ${viewHead(
      'Cost of harm',
      'Which industries hurt people often, and which hurt them expensively?',
      `Safe Work Australia publishes how often an industry injures people and what those injuries cost, in two separate tables it never crosses. Crossed, they separate industries that produce a steady stream of recoverable injuries from industries that injure people rarely but ruinously — and the two need completely different responses.`,
    )}

    <div class="ctl-row">
      ${segmented('measure', [
        { value: 'comp', label: 'Cost in dollars' },
        { value: 'lost', label: 'Cost in time off work' },
      ], yMeasure)}
      <span class="ctl-note">${period(P[idx])} · ANZSIC subdivisions with at least 200 claims · bubble size is the number of claims</span>
    </div>

    ${card(
      `Frequency against ${yMeasure === 'comp' ? 'cost' : 'time lost'}`,
      `Each bubble is one industry subdivision. The crosshair is the median industry on each axis, so the four quadrants are defined by the data rather than by a round number I picked. The vertical axis is logarithmic — ${gloss('median compensation', 'median compensation')} spans an order of magnitude, and a linear axis would crush two thirds of the industries into the bottom third of the chart. Scroll to zoom, drag to pan, double-click to reset. Click any bubble for the full record.`,
      `<div class="scatter-wrap">${chartBox(scatter(b), 'xwide')}</div>` +
        legend(
          divisionsPresent.map((d) => ({
            color: DIVISION_COLORS[d] ?? '#8b96a0',
            label: `${d} — ${rows.find((r) => r.division === d)?.divisionName ?? d}`,
          })),
        ),
    )}

    ${card(
      'Rare, but ruinous',
      `${rareRuinous.length} industries injure people less often than the median and cost more per injury than the median. A safety programme judged on claim counts alone never reaches them.`,
      list(rareRuinous, idx, P, yMeasure),
    )}

    ${card(
      'Frequent, but recoverable',
      `${oftenCheap.length} industries do the opposite: a steady stream of injuries that people recover from comparatively quickly and cheaply. The volume is the problem here, not the severity.`,
      list(oftenCheap, idx, P, yMeasure),
    )}

    ${note(
      `<strong>Why the newest year is not shown.</strong> Both cost measures are withheld for ${period(P[P.length - 1])} because compensation and time off work keep accruing after a claim year closes. The frequency rate is published for that year, but pairing a final cost with a preliminary frequency would compare two different things, so this view uses ${period(P[idx])} for both.`,
    )}
  `;
}

function list(rows: Row[], idx: number, _P: string[], measure: 'comp' | 'lost'): string {
  if (!rows.length) return `<p class="empty">No industries fall in this quadrant.</p>`;
  const sorted = [...rows].sort((a, b) => (b[measure]![idx] as number) - (a[measure]![idx] as number)).slice(0, 8);
  return `<div class="tbl-wrap"><table class="data" style="min-width:560px">
    <thead><tr><th scope="col">Industry</th><th scope="col">Division</th><th scope="col" class="num">Claims/M hrs</th><th scope="col" class="num">${measure === 'comp' ? 'Median paid' : 'Median weeks'}</th><th scope="col" class="num">Claims</th></tr></thead>
    <tbody>${sorted
      .map(
        (r) => `<tr class="clickable" data-code="${esc(r.code)}"${rowActivationAttrs(`${r.name}. Open the full industry record.`)} data-tip="${tipAttr(`${r.name}\nClick for the full industry record`)}">
          <td class="wide">${esc(r.name)}</td>
          <td><span class="swatch-inline" style="background:${divisionColor(r.division)}"></span>${esc(r.divisionName)}</td>
          <td class="num">${rate(r.freq![idx], 2)}</td>
          <td class="num">${measure === 'comp' ? money(r[measure]![idx]) : weeks(r[measure]![idx])}</td>
          <td class="num">${num(r.count[idx])}</td>
        </tr>`,
      )
      .join('')}</tbody></table></div>`;
}

export function mount(root: HTMLElement, b: Bundle, rerender: () => void): void {
  const svg = root.querySelector<SVGSVGElement>('.scatter-svg');
  if (svg) attachSvgZoom(svg, { maxScale: 12 });

  root.querySelectorAll<HTMLButtonElement>('[data-seg="measure"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      yMeasure = btn.dataset.value === 'lost' ? 'lost' : 'comp';
      rerender();
    });
  });

  const open = (code: string) => {
    const row = withDivision(b.claims.industry).find((r) => r.code === code);
    if (row) openIndustryDrawer(b, row);
  };

  root.querySelectorAll<SVGCircleElement>('.dot[data-code]').forEach((dot) => {
    dot.addEventListener('click', () => open(dot.dataset.code ?? ''));
    dot.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(dot.dataset.code ?? ''); }
    });
  });

  root.querySelectorAll<HTMLTableRowElement>('tr.clickable[data-code]').forEach((tr) => {
    tr.addEventListener('click', () => open(tr.dataset.code ?? ''));
    tr.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(tr.dataset.code ?? ''); }
    });
  });
}
